import { useContext, useEffect, useState } from "react";
import { ToastContext } from "../../../../hooks/context";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import {
  cleanObject,
  extractDateOnly,
  truncateString,
} from "../../../../helpers";
import {
  useCreateGRMutation,
  useUpdateGRMutation,
} from "../../../../hooks/api/mutations";
import {
  useGetGoodsReceivedItems,
  useGetPurchaseOrderDetails,
} from "../../../../hooks/api/queries";
import { useUploadImageMutation } from "../../../../hooks/api/mutations/useMedia";
import { useQueryClient } from "react-query";

export const useGoodsReceivedForm = ({
  isEditMode = false,
  GRDetails = {},
}) => {
  const triggerToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();
  const queryClient = useQueryClient();

  const [gRForm, setGRForm] = useState({
    po_id: GRDetails?.purchase_order?.id || search.get("po_id") || "",
    po_number:
      GRDetails?.purchase_order?.po_number || search.get("po_number") || "",
    gr_date: GRDetails?.gr_date ? extractDateOnly(GRDetails?.gr_date) : "",
    waybill_url: GRDetails?.waybill_url || null,
    waybill_no: GRDetails?.waybill_no || "",
    waybill_driver_name: GRDetails?.waybill_driver_name || "",
    waybill_driver_phone_code: GRDetails?.waybill_driver_phone_code || "+234",
    waybill_driver_phone_number: GRDetails?.waybill_driver_phone_number || "",
    file_name: GRDetails?.waybill_url
      ? truncateString(GRDetails?.waybill_url, 25)
      : null,
    notes: GRDetails?.notes || "",
    items: [],
  });

  const { data: gRItems } = useGetGoodsReceivedItems({
    id: isEditMode ? id : gRForm?.po_id,
    isEditMode,
  });

  const { data: pODetails } = useGetPurchaseOrderDetails({
    id: gRForm?.po_id,
  });

  const { mutate: createGR, isLoading: isCreatingGR } = useCreateGRMutation();
  const { mutate: UpdateGR, isLoading: isUpdatingGR } = useUpdateGRMutation({
    id: GRDetails?.id,
  });

  const { mutate: uploadWaybill, isLoading: isLoadingWaybillFile } =
    useUploadImageMutation({});

  const getDataForSubmission = () => {
    return cleanObject({
      ...gRForm,
      phone_code: gRForm?.waybill_driver_phone_number !== "" ? "+234" : "",
      items: gRForm?.items?.map((item) =>
        cleanObject({
          quantity_received: item?.quantity_received,
          po_item_id: item?.po_item_id,
          expiry_date: item?.expiry_date,
          items_number: item?.items_number,
          batch_name: item?.batch_name,
          barcode: item?.barcode,
        }),
      ),
    });
  };

  const handleCreateGR = (status) => {
    // Logic to handle the creation of a Purchase Order
    if (gRForm?.items?.length === 0)
      return triggerToast("Please add items to the Goods received", "warning");

    const data = getDataForSubmission();

    createGR(
      { ...data, status },
      {
        onSuccess: (res) => {
          triggerToast(
            "Your Purchase Order has successfully been created",
            "success",
          );
          const gRId = res?.data?.data?.id;
          const query = status === "IN_REVIEW" ? `?new_gr_id=${gRId}` : "";

          queryClient.invalidateQueries([
            "getPOGoodsReceivedItems",
            `${gRForm?.po_id}`,
          ]);

          navigate(
            pathConstants.INVENTORY_GOODS_RECEIVED_DETAILS({
              id: res?.data?.data?.id,
            }) + query,
          );
        },
      },
    );
  };

  const handleUpdateGR = (status) => {
    // Logic to handle the updating of a Purchase Order
    if (gRForm?.items.length === 0)
      return triggerToast("Please add items to the Goods Received", "warning");

    const data = getDataForSubmission();

    UpdateGR(
      { id: GRDetails?.id, data: { ...data, status } },
      {
        onSuccess: () => {
          triggerToast(
            `Goods Received: (${GRDetails?.gr_number}) updated successfully`,
            "success",
          );

          const query =
            status === "IN_REVIEW" ? `?new_gr_id=${GRDetails?.id}` : "";

          navigate(
            pathConstants.INVENTORY_GOODS_RECEIVED_DETAILS({
              id: GRDetails?.id,
            }) + query,
          );
        },
      },
    );
  };

  const createOrUpdateGR = (status = "IN_REVIEW") => {
    if (isEditMode) handleUpdateGR(status);
    else handleCreateGR(status);
  };

  const handleUploadWaybill = (document, fileName) => {
    if (!document)
      return setGRForm((prev) => ({
        ...prev,
        waybill_url: "",
        file_name: "",
      }));

    const formData = new FormData();
    formData.append("file", document);
    uploadWaybill(
      { formData },
      {
        onSuccess: (res) => {
          setGRForm((prev) => ({
            ...prev,
            waybill_url: res?.data?.data?.url,
            file_name: fileName,
          }));
          triggerToast("Document uploaded successfully", "success");
        },
      },
    );
  };

  useEffect(() => {
    if (gRItems) setGRForm((prev) => ({ ...prev, items: gRItems }));
  }, [gRItems]);

  return {
    isSubmittingGR: isCreatingGR || isUpdatingGR,
    isLoadingWaybillFile,
    gRForm,
    pODetails,
    setGRForm,
    createOrUpdateGR,
    handleUploadWaybill,
    navigate,
  };
};
